import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import accountApi from "@/lib/accountApiClient";
import { useAccount } from "@/lib/AccountContext";
import { recoveryErrorKind, useRecoveryTokenFromUrl } from "@/lib/accountRecovery";
import { validateNewPassword } from "@/lib/accountValidation";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AccountResetPassword() {
  const { t } = useLang();
  const { clearAccountSession } = useAccount();
  const navigate = useNavigate();
  const token = useRecoveryTokenFromUrl();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(() => token ? "" : t("account.recovery.error.invalid"));

  useSEO({
    title: `${t("account.reset.title")} — Mindovia`,
    description: t("account.reset.description"),
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const validationError = validateNewPassword(password, confirmation);
    if (validationError) {
      setError(t(`account.validation.${validationError}`));
      return;
    }
    if (!token) {
      setError(t("account.recovery.error.invalid"));
      return;
    }
    setSubmitting(true);
    try {
      await accountApi.post("/account/auth/reset-password", { token, new_password: password });
      clearAccountSession();
      toast.success(t("account.reset.success"));
      navigate("/login", { replace: true });
    } catch (requestError) {
      setError(t(`account.recovery.error.${recoveryErrorKind(requestError)}`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] px-4 py-12 sm:px-6 sm:py-16" data-wrap="responsive">
      <section className="mx-auto max-w-md rounded-3xl border border-border bg-card p-6 shadow-lg sm:p-8">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-sage-100 text-teal-800"><KeyRound className="h-5 w-5" /></div>
        <h1 className="mt-5 font-display text-3xl tracking-tight">{t("account.reset.title")}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("account.reset.description")}</p>
        <form onSubmit={submit} className="mt-7 space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="reset-password">{t("account.reset.new_password")}</Label>
            <Input id="reset-password" type="password" autoComplete="new-password" required value={password} onChange={(event) => setPassword(event.target.value)} className="h-12" />
            <p className="text-xs text-muted-foreground">{t("account.password_help")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirmation">{t("account.confirm_password")}</Label>
            <Input id="reset-confirmation" type="password" autoComplete="new-password" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="h-12" />
          </div>
          {error && <div role="alert" aria-live="polite" className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}
          <Button type="submit" disabled={submitting || !token} aria-busy={submitting} className="h-12 w-full rounded-full bg-teal-800 text-cream-50 hover:bg-teal-700">
            {submitting ? t("account.working") : t("account.reset.submit")}
          </Button>
        </form>
        <Link to="/account/forgot-password" className="mt-6 inline-block text-sm font-medium text-terracotta-700 hover:underline">{t("account.reset.request_new")}</Link>
      </section>
    </div>
  );
}
